import { Dropdown, Button, Tooltip } from 'antd';
import { MoreOutlined, DeleteOutlined, EyeOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { InvoiceDetailRoute } from '../../routes/routepath';

/* ─── Types ──────────────────────────────────────────────────────────── */

export interface CreditNoteRecord {
  _id?:               string;
  creditNoteNumber?:  string;
  invoiceId?: {
    _id?:            string;
    invoiceNumber?:  string;
    salesPersonId?:  { name?: string };
    discountAmount?: number;
    startDate?:      string;
    invoiceType?:    string;
  };
  membershipId?:      { type?: string };
  serviceType?:       string;
  trainerName?:       string;
  branchId?:          { name?: string };
  userId?:            { name?: string; phoneNumber?: string };
  customerName?:      string;
  customerNumber?:    string;
  packageName?:       string;
  invoiceAmount?:     number;
  refundAmount?:      number;
  expiryDate?:        string;
  paymentRefundDate?: string;
  remarksType?:       'text' | 'image' | 'image and text';
  remarksText?:       string;
  remarksImage?:      string;
  createdBy?:         { name?: string };
}

/* ─── Helpers ────────────────────────────────────────────────────────── */

function formatDate(date: string | undefined): string {
  if (!date) return '-';
  const d = new Date(date);
  if (isNaN(d.getTime())) return '-';
  return `${String(d.getDate()).padStart(2, '0')}-${String(d.getMonth() + 1).padStart(2, '0')}-${d.getFullYear()}`;
}

/* ─── Columns ────────────────────────────────────────────────────────── */

export const getCreditNoteColumns = (
  handleViewRemark: (record: CreditNoteRecord) => void,
  handleDelete:      (record: CreditNoteRecord) => void,
  canDelete = false,
) => [
  {
    title:     'Credit No.',
    dataIndex: 'creditNoteNumber',
    key:       'creditNoteNumber',
    width:     140,
    render:    (v?: string) => v || '-',
  },
  {
    title:     'Against INV.',
    key:       'againstInvoice',
    width:     150,
    render:    (_: unknown, record: CreditNoteRecord) => record.invoiceId?.invoiceNumber
      ? (
        <Link
          to={`${InvoiceDetailRoute}/${record.invoiceId._id}?from=credit-note`}
          state={{ creditNote: record }}
          style={{ color: '#1890ff' }}
        >
          {record.invoiceId.invoiceNumber}
        </Link>
      )
      : '-',
  },
  {
    title:     'Payment Refund Date',
    dataIndex: 'paymentRefundDate',
    key:       'paymentRefundDate',
    width:     150,
    render:    (date: string) => formatDate(date),
  },
  {
    title:     'Branch Name',
    key:       'branchName',
    width:     150,
    render:    (_: unknown, record: CreditNoteRecord) => record.branchId?.name || '-',
  },
  {
    title:     'Name',
    key:       'customerName',
    width:     150,
    render:    (_: unknown, record: CreditNoteRecord) => record.userId?.name || record.customerName || '-',
  },
  {
    title:     'Mobile No.',
    key:       'customerMobile',
    width:     120,
    render:    (_: unknown, record: CreditNoteRecord) => record.userId?.phoneNumber || record.customerNumber || '-',
  },
  {
    title:     'Service Type',
    dataIndex: 'serviceType',
    key:       'serviceType',
    width:     130,
    render:    (v?: string) => v || '-',
  },
  {
    title:     'Trainer Name',
    dataIndex: 'trainerName',
    key:       'trainerName',
    width:     150,
    render:    (v?: string) => v || '-',
  },
  {
    title:     'Sales Person',
    key:       'salesPersonName',
    width:     150,
    ellipsis:  { showTitle: false },
    render:    (_: unknown, record: CreditNoteRecord) => {
      const name = record.invoiceId?.salesPersonId?.name || '-';
      return name === '-' ? name : <Tooltip title={name}>{name}</Tooltip>;
    },
  },
  {
    title:     'Creditor',
    key:       'creditor',
    width:     130,
    render:    (_: unknown, record: CreditNoteRecord) => record.createdBy?.name || '-',
  },
  {
    title:     'Package Name',
    dataIndex: 'packageName',
    key:       'packageName',
    width:     150,
    render:    (v?: string) => v || '-',
  },
  {
    title:     'Start Date',
    key:       'startDate',
    width:     110,
    render:    (_: unknown, record: CreditNoteRecord) => formatDate(record.invoiceId?.startDate),
  },
  {
    title:     'Expiry Date',
    dataIndex: 'expiryDate',
    key:       'expiryDate',
    width:     110,
    render:    (date: string) => formatDate(date),
  },
  {
    title:     'Invoice Amount(₹)',
    dataIndex: 'invoiceAmount',
    key:       'packagePrice',
    width:     140,
    render:    (v?: number) => v ? `₹${v.toLocaleString()}` : '₹0',
  },
  {
    title:     'Refund Amount(₹)',
    dataIndex: 'refundAmount',
    key:       'refundAmount',
    width:     150,
    render:    (v?: number) => v ? `₹${v.toLocaleString()}` : '₹0',
  },
  {
    title:  'Remark',
    key:    'remark',
    width:  90,
    align:  'center' as const,
    render: (_: unknown, record: CreditNoteRecord) => (
      (record.remarksText || record.remarksImage) ? (
        <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => handleViewRemark(record)}>
          View
        </Button>
      ) : '-'
    ),
  },
  {
    title:  'View',
    key:    'view',
    width:  80,
    align:  'center' as const,
    render: (_: unknown, record: CreditNoteRecord) => record.invoiceId?._id ? (
      <Link
        to={`${InvoiceDetailRoute}/${record.invoiceId._id}?from=credit-note`}
        state={{ creditNote: record }}
        style={{ color: '#1890ff' }}
      >
        <Button type="text" icon={<EyeOutlined />} size="small" />
      </Link>
    ) : '-',
  },
  {
    title:  'Actions',
    key:    'actions',
    width:  90,
    fixed:  'right' as const,
    render: (_: unknown, record: CreditNoteRecord) => {
      const items = [
        ...(canDelete ? [{
          key:     'delete',
          label:   (
            <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <DeleteOutlined />
              Delete
            </span>
          ),
          onClick: () => handleDelete(record),
          danger:  true,
        }] : []),
      ];
      if (!items.length) return '-';
      return (
        <Dropdown menu={{ items }} trigger={['click']} placement="bottomRight">
          <Button type="text" icon={<MoreOutlined />} size="small" />
        </Dropdown>
      );
    },
  },
];
